import { z } from "zod";

// The full set of message kinds. Mirrored in:
//   - migrations/092_messages_typed_kinds.sql (Postgres enum message_kind)
//   - apps/native/ios/Ano/Core/Models/MessagePayload.swift (Swift Codable)
//   - apps/native/ios/Ano/Core/Models/MessagePredicates.swift (Swift predicates)
export const MESSAGE_KINDS = [
  "text",

  // Media kinds (single primary media per message; media_group for mixed sets)
  "image",
  "video",
  "audio",
  "gif",
  "file",
  "media_group",

  // URL-only messages (user explicitly posts a link, distinct from text with inline URLs)
  "link_only",

  // Former <!--card:--> types, each first-class
  "email",
  "calendar",
  "terminal_output",
  "terminal_snapshot",
  "terminal_live",
  "terminal_recording",
  "run",
  "command_history",

  // Call lifecycle events (server-only — clients can never insert these)
  "call_started_video",
  "call_started_audio",
  "call_ended",

  // Catch-all for rare system events (member promoted, integration connected, etc.)
  "system",
] as const;

export type MessageKind = (typeof MESSAGE_KINDS)[number];

export const messageKindSchema = z.enum(MESSAGE_KINDS);

// Kinds that only the server may stamp. Clients attempting to insert these
// must be rejected by the mutator (Phase 2 enforcement gate).
export const SERVER_ONLY_KINDS = new Set<MessageKind>([
  "system",
  "call_started_video",
  "call_started_audio",
  "call_ended",
]);

// ---------------------------------------------------------------------------
// Per-kind payload Zod schemas
//
// Each schema covers fields that are NOT promoted to top-level columns on the
// messages table. Queryable / filterable fields (calendar_start_at, email_subject,
// terminal_exit_code, call_duration_seconds, etc.) live on the row itself.
//
// `.passthrough()` on payload object schemas keeps forward-compat: new render-only
// fields added in later phases land in payload without breaking existing writers.
// Phase 2's builder helpers do the strict per-kind validation before insert.
// ---------------------------------------------------------------------------

export const calendarPayloadSchema = z
  .object({
    location: z.string().optional(),
    conferencing_url: z.string().url().optional(),
    recurrence_rule: z.string().optional(),
  })
  .passthrough();

export const emailPayloadSchema = z
  .object({
    html_body: z.string().optional(),
    in_reply_to: z.string().optional(),
    message_id: z.string().optional(),
    headers: z.record(z.string(), z.string()).optional(),
  })
  .passthrough();

export const terminalPayloadSchema = z
  .object({
    cwd: z.string().optional(),
    env: z.record(z.string(), z.string()).optional(),
    duration_ms: z.number().optional(),
    ansi: z.string().optional(),
  })
  .passthrough();

export const callPayloadSchema = z
  .object({
    recording_url: z.string().url().optional(),
    call_quality_stats: z.unknown().optional(),
  })
  .passthrough();

export const mediaGroupPayloadSchema = z.object({
  items: z
    .array(
      z.object({
        attachment_id: z.string().min(1),
        caption: z.string().optional(),
      }),
    )
    .min(1),
});

// Phase 7b — single-media kinds may stamp payload=null (single attachment, no
// gallery) or payload.items (gallery of same-category attachments). Items
// reuse the mediaGroup item-ref shape: { attachment_id, caption? }.
const mediaItemRefSchema = z.object({
  attachment_id: z.string().min(1),
  caption: z.string().optional(),
});

// `.min(1)` on items[] mirrors mediaGroupPayloadSchema (line ~98). When
// the producer stamps a payload.items[] array (multi-attachment galleries)
// it always has ≥ 2 entries, but the array could be empty if a future
// caller bypasses derive and stamps `{ items: [] }` directly. An empty-
// gallery payload would render as zero attachments — silently broken.
// The constraint forces explicit shape: payload is null OR items has rows.
export const imagePayloadSchema = z.object({
  items: z.array(mediaItemRefSchema).min(1).optional(),
});

export const videoPayloadSchema = z.object({
  items: z.array(mediaItemRefSchema).min(1).optional(),
});

export const audioPayloadSchema = z.object({
  items: z.array(mediaItemRefSchema).min(1).optional(),
});

export const filePayloadSchema = z.object({
  items: z.array(mediaItemRefSchema).min(1).optional(),
});

// Phase 7b PR C — gif payload for /giphy and external gif sources. Picker
// `.gif` uploads stamp payload=null (renderer falls back to attachment row).
// Same http/https refine as linkOnlyPayloadSchema for XSS defense.
export const gifPayloadSchema = z.object({
  url: z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), {
      message: "url must use http or https scheme",
    }),
  search_term: z.string().optional(),
});

// Defense-in-depth: the producer-side URL_ONLY_RE in
// message-kind-derivation.ts only matches `https?://` so the derive path
// cannot produce non-http(s) URLs. But explicit-kind callers (iOS, REST
// /api/messages, etc.) bypass derive and could otherwise stamp
// payload.url with a `javascript:` or `data:` URL — z.string().url()
// accepts both. Reject those at the schema layer so a future renderer
// that plugs payload.url into an href attribute doesn't have a latent
// XSS surface.
export const linkOnlyPayloadSchema = z.object({
  url: z
    .string()
    .url()
    .refine((u) => /^https?:\/\//i.test(u), {
      message: "url must use http or https scheme",
    }),
});

export const systemPayloadSchema = z.object({
  event: z.string().min(1),
  actors: z.array(z.string()).optional(),
  data: z.record(z.string(), z.unknown()).optional(),
  // Discord-shape thread-created announcement (PR4). When `event ===
  // "thread_created"`, these point readers at the source message and
  // the user who started the thread. Extra knobs the renderer needs.
  thread_message_id: z.string().optional(),
  started_by_user_id: z.string().optional(),
});

// Top-level payload Zod schema. Phase 1 keeps this permissive (null or any object)
// so writers can start stamping `kind` + `payload` without locking into a strict
// discriminated union before the builder layer exists. Phase 2 will tighten this
// via per-kind discrimination inside message-builders.ts, where each builder maps
// 1:1 to the per-kind schema above.
export const messagePayloadSchema = z
  .union([z.null(), z.record(z.string(), z.unknown())])
  .optional();
